/**
 * Invite codes.
 *
 * Signup is invite-gated, so this screen is the front door: without a code
 * nobody can join. Two jobs on one page — mint a batch and hand it out, then
 * see what happened to the ones already out there. Minted codes are shown
 * once, big and copyable, because that is the only moment they are useful.
 */
import { useCallback, useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { BackOfficeNav } from "../components/BackOfficeNav";
import { Meter, MeterRow } from "../components/Meter";
import { Pagination } from "../components/Pagination";
import { RefreshButton } from "../components/RefreshButton";
import { ReviewSignIn } from "../components/ReviewSignIn";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { useToast } from "../hooks/useToast";
import { inviteAdmin, type ReviewCreds } from "../api/endpoints";
import { clearReviewCredentials, readReviewCredentials } from "../lib/session";
import { isCredentialFailure } from "../lib/backOffice";
import { copyText } from "../lib/clipboard";
import { formatDate } from "../lib/validation";
import type { InviteCode, InviteCodeList, InviteStatus } from "../types/api";

const FILTERS: Array<{ id: InviteStatus | "all"; label: string }> = [
  { id: "available", label: "Available" },
  { id: "used", label: "Used" },
  { id: "expired", label: "Expired" },
  { id: "revoked", label: "Withdrawn" },
  { id: "all", label: "All" },
];

const STATUS_PILL: Record<InviteStatus, { label: string; className: string }> = {
  available: { label: "Available", className: "pill-approved" },
  used: { label: "Used", className: "" },
  expired: { label: "Expired", className: "pill-pending" },
  revoked: { label: "Withdrawn", className: "pill-rejected" },
};

export function AdminInvites() {
  const toast = useToast();
  const [creds, setCreds] = useState<ReviewCreds | null>(() => readReviewCredentials());
  const [data, setData] = useState<InviteCodeList | null>(null);
  const [filter, setFilter] = useState<InviteStatus | "all">("available");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);

  // The mint form.
  const [count, setCount] = useState(10);
  const [maxUses, setMaxUses] = useState(1);
  const [expiresDays, setExpiresDays] = useState("");
  const [note, setNote] = useState("");
  const [minted, setMinted] = useState<string[] | null>(null);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    const timer = window.setTimeout(
      () => {
        void (async () => {
          try {
            setLoadError(null);
            const result = await inviteAdmin.list(creds, {
              status: filter === "all" ? undefined : filter,
              search: search.trim() || undefined,
              page,
            });
            if (!cancelled) setData(result);
          } catch (err) {
            if (cancelled) return;
            // A rejected key means sign in again; anything else is transient
            // and must not throw an admin out of the screen.
            if (isCredentialFailure(err)) {
              clearReviewCredentials();
              setCreds(null);
            } else {
              setLoadError((err as Error).message);
            }
          } finally {
            if (!cancelled) setLoading(false);
          }
        })();
      },
      // Debounced while typing in the search box, immediate otherwise.
      search ? 350 : 0,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [creds, filter, search, page, nonce]);

  // Never while a batch is being minted or a code withdrawn — a refresh
  // mid-action would replace the list under whoever is reading it.
  useRefreshOnFocus(reload, { enabled: busy === null });

  async function mint() {
    if (!creds) return;
    setBusy("mint");
    try {
      const days = Number.parseInt(expiresDays, 10);
      const result = await inviteAdmin.mint(creds, {
        count,
        max_uses: maxUses,
        expires_days: Number.isFinite(days) && days > 0 ? days : undefined,
        note: note.trim() || undefined,
      });
      setMinted(result.codes);
      reload();
      toast.show(
        `Minted ${result.codes.length} code${result.codes.length === 1 ? "" : "s"}.`,
        "success",
      );
    } catch (err) {
      toast.show((err as Error).message, "error");
      if (isCredentialFailure(err)) {
        clearReviewCredentials();
        setCreds(null);
      }
    } finally {
      setBusy(null);
    }
  }

  async function setRevoked(code: string, revoked: boolean) {
    if (!creds) return;
    setBusy(code);
    try {
      await inviteAdmin.setRevoked(creds, code, revoked);
      toast.show(revoked ? `${code} withdrawn.` : `${code} is usable again.`, "success");
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  async function copy(text: string, label: string) {
    // Say what actually happened: a silent no-op leaves someone pasting
    // whatever was on their clipboard before and never noticing.
    const copied = await copyText(text);
    toast.show(
      copied ? `${label} copied.` : "Couldn't copy — select the code instead.",
      copied ? "success" : "error",
    );
  }

  if (!creds) return <ReviewSignIn onSubmit={setCreds} />;

  return (
    <Layout showNav={false} showAccount={false} title="Invites">
      <section className="section">
        <div className="wrap">
          <BackOfficeNav />

          <div className="row-between">
            <div className="eyebrow">Invite codes</div>
            <RefreshButton onClick={reload} busy={loading} />
          </div>
          <h1 className="display" style={{ fontSize: "clamp(22px,4vw,32px)", margin: "0 0 18px" }}>
            Who can join
          </h1>

          <MeterRow>
            <Meter label="Available" value={data?.counts.available.toLocaleString()} />
            <Meter label="Used" value={data?.counts.used.toLocaleString()} />
            <Meter
              label="Expired or withdrawn"
              value={
                data && (data.counts.expired + data.counts.revoked).toLocaleString()
              }
            />
          </MeterRow>

          <div className="panel mt-24">
            <div className="card-label">Mint a batch</div>

            <div className="invite-form">
              <div className="field">
                <label htmlFor="invite-count">How many codes</label>
                <input
                  id="invite-count"
                  type="number"
                  min={1}
                  max={500}
                  value={count}
                  onChange={(event) => setCount(Number(event.target.value))}
                />
              </div>

              <div className="field">
                <label htmlFor="invite-uses">Sign-ups per code</label>
                <input
                  id="invite-uses"
                  type="number"
                  min={1}
                  max={1000}
                  value={maxUses}
                  onChange={(event) => setMaxUses(Number(event.target.value))}
                />
                <div className="hint">
                  Leave at 1 for one code each. Raise it to share a single code with a group.
                </div>
              </div>

              <div className="field">
                <label htmlFor="invite-expiry">Expires in (days)</label>
                <input
                  id="invite-expiry"
                  type="number"
                  min={1}
                  max={365}
                  value={expiresDays}
                  placeholder="Never"
                  onChange={(event) => setExpiresDays(event.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="invite-note">Note (optional)</label>
                <input
                  id="invite-note"
                  value={note}
                  maxLength={200}
                  placeholder="Who these are for"
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary mt-16"
              style={{ width: "auto" }}
              disabled={busy !== null || count < 1}
              onClick={() => void mint()}
            >
              {busy === "mint"
                ? "Minting…"
                : `Mint ${count} code${count === 1 ? "" : "s"}`}
            </button>
          </div>

          {minted && (
            <div className="panel mt-24 minted">
              <div className="row-between">
                <div className="card-label" style={{ margin: 0 }}>
                  Just minted — copy them now
                </div>
                <div className="btn-row">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => void copy(minted.join("\n"), "All codes")}
                  >
                    Copy all
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setMinted(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <div className="minted-codes">
                {minted.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className="minted-code mono"
                    title="Copy"
                    onClick={() => void copy(code, code)}
                  >
                    {code}
                  </button>
                ))}
              </div>
              <div className="hint" style={{ textAlign: "left" }}>
                They stay in the list below, so nothing is lost if you close this.
              </div>
            </div>
          )}

          <div className="filter-row mt-24">
            {FILTERS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`chip ${filter === entry.id ? "chip-active" : ""}`}
                onClick={() => {
                  setFilter(entry.id);
                  setPage(1);
                }}
              >
                {entry.label}
                {entry.id !== "all" && data ? ` (${data.counts[entry.id]})` : ""}
              </button>
            ))}
          </div>

          <div className="field mt-16">
            <input
              value={search}
              placeholder="Search for a code…"
              aria-label="Search invite codes"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>

          {loading && !data ? (
            <div className="stack mt-16" aria-busy="true" aria-label="Loading invite codes">
              {[0, 1, 2].map((row) => (
                <div key={row} className="panel panel-tight log-skeleton">
                  <div className="skeleton-line skeleton-title" />
                  <div className="skeleton-line skeleton-meta" />
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div className="panel empty mt-16">
              <h3>Couldn't load the codes</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>{loadError}</p>
              <button type="button" className="btn btn-ghost mt-16" onClick={reload}>
                Try again
              </button>
            </div>
          ) : data && data.codes.length === 0 ? (
            <div className="panel empty mt-16">
              <h3>{search ? "No code matches that" : "Nothing here"}</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                {filter === "available"
                  ? "No unused codes left — mint a batch above."
                  : "Nothing with that status."}
              </p>
            </div>
          ) : (
            <div className="stack mt-16">
              {data?.codes.map((invite) => (
                <InviteRow
                  key={invite.code}
                  invite={invite}
                  busy={busy === invite.code}
                  onCopy={() => void copy(invite.code, invite.code)}
                  onToggleRevoked={() => void setRevoked(invite.code, !invite.revoked)}
                />
              ))}
            </div>
          )}

          {data && (
            <Pagination
              page={data.page}
              // No has_more on this list, so derive it from the total.
              hasMore={data.page * data.page_size < data.total}
              total={data.total}
              noun={data.total === 1 ? "code" : "codes"}
              busy={loading}
              onChange={setPage}
            />
          )}
        </div>
      </section>
    </Layout>
  );
}

function InviteRow({
  invite,
  busy,
  onCopy,
  onToggleRevoked,
}: {
  invite: InviteCode;
  busy: boolean;
  onCopy: () => void;
  onToggleRevoked: () => void;
}) {
  const pill = STATUS_PILL[invite.status];
  return (
    <article className="panel panel-tight invite-row">
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <button type="button" className="invite-code mono" title="Copy" onClick={onCopy}>
            {invite.code}
          </button>
          <div className="log-meta muted">
            {invite.max_uses > 1
              ? `${invite.uses} of ${invite.max_uses} used`
              : invite.uses > 0
                ? "Redeemed"
                : "Not used yet"}
            {invite.note ? ` · ${invite.note}` : ""} · minted {formatDate(invite.created_at)}
            {invite.expires_at ? ` · expires ${formatDate(invite.expires_at)}` : ""}
          </div>
        </div>
        <span className={`pill ${pill.className}`}>{pill.label}</span>
      </div>

      <div className="row-between mt-8">
        {/* Names, not counts: "1 contributor joined" is not something anybody
            can act on, and a bare id is worse. */}
        <span className="muted" style={{ fontSize: 12 }}>
          {invite.redeemed_by.length > 0
            ? `Used by ${invite.redeemed_by
                .map((entry) => entry.name ?? entry.contributor_id)
                .join(", ")}`
            : ""}
        </span>
        {/* A used-up code cannot be withdrawn — there is nothing left to stop,
            and the account it created stays either way. */}
        {(invite.status === "available" || invite.status === "revoked") && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={onToggleRevoked}
          >
            {busy ? "Saving…" : invite.revoked ? "Put back" : "Withdraw"}
          </button>
        )}
      </div>
    </article>
  );
}
